/** Dashboard va Reports API. Real-time hodisalar kelganda dashboard 1.5 soniya ichida qayta so'raladi. */
import { useEffect } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getAllPages, type RequestOptions } from "@/lib/api";
import { subscribeEvents } from "@/lib/realtime";
import i18n from "i18next";
import { dayjs } from "@/lib/utils";
import type { DashboardData, ReportData, ReportSection } from "./types";

export type DashboardQuery = RequestOptions["query"];

export function useDashboard(query: DashboardQuery) {
  const qc = useQueryClient();
  useEffect(() => {
    // Driver, eslatma yoki tasdiq o'zgarsa raqamlar eskiradi; ketma-ket hodisalar bitta so'rovga yig'iladi
    let timer: number | undefined;
    const off = subscribeEvents(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => qc.invalidateQueries({ queryKey: ["dashboard"] }), 1500);
    });
    return () => {
      window.clearTimeout(timer);
      off();
    };
  }, [qc]);
  return useQuery({
    queryKey: ["dashboard", query],
    queryFn: () => api.get<DashboardData>("/api/dashboard/", { query }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useReport<S extends ReportSection>(section: S, query: DashboardQuery, enabled = true) {
  return useQuery({
    queryKey: ["report", section, query],
    queryFn: () => api.get<ReportData[S]>(`/api/reports/${section}/`, { query }),
    staleTime: 30_000,
    enabled,
    placeholderData: keepPreviousData,
  });
}

export interface BranchOption {
  id: number;
  name: string;
}

/** AppShell bilan bir xil kalit va so'rov: kesh umumiy */
export function useBranchOptions(enabled = true) {
  return useQuery({
    queryKey: ["branches", "options"],
    queryFn: () => getAllPages<BranchOption>("/api/branches/"),
    enabled,
    staleTime: 10 * 60_000,
  });
}



// ---- Davr (oy) yordamchilari ----

export function currentPeriod(): string {
  return dayjs().format("YYYY-MM");
}

export function periodOptions(count = 12): { value: string; label: string }[] {
  return Array.from({ length: count }, (_, i) => {
    const d = dayjs().subtract(i, "month");
    return { value: d.format("YYYY-MM"), label: d.format("MMMM YYYY") };
  });
}

// ---- Hisobot davri: oy (2026-10), chorak (2026-Q4), yil (2026), oraliq (2026-09-01_2026-10-11) ----
// Backend bilan bir xil (apps/dashboards/metrics.Period). Test: period.test.ts

export type PeriodKind = "month" | "quarter" | "year" | "range";

export function periodKind(period: string): PeriodKind {
  if (/^\d{4}-Q[1-4]$/.test(period)) return "quarter";
  if (/^\d{4}$/.test(period)) return "year";
  if (/^\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/.test(period)) return "range";
  return "month";
}

export function currentQuarter(): string {
  const d = dayjs();
  return `${d.year()}-Q${Math.floor(d.month() / 3) + 1}`;
}

/** Davr nomi ko'rinib turgan tilda: "Oktabr 2026", "4-chorak 2026", "2026", "20 Sen 2026 – 5 Okt 2026" */
export function periodLabel(period: string): string {
  const kind = periodKind(period);
  if (kind === "quarter") return i18n.t("common:periods.quarter", { q: period.slice(-1), year: period.slice(0, 4) });
  if (kind === "year") return period;
  if (kind === "range") {
    const [a, b] = period.split("_");
    return `${dayjs(a).format("D MMM YYYY")} – ${dayjs(b).format("D MMM YYYY")}`;
  }
  return dayjs(`${period}-01`).format("MMMM YYYY");
}

/** Xuddi shu uzunlikdagi oldingi davr (solishtirish uchun) */
export function previousPeriod(period: string): string {
  const kind = periodKind(period);
  if (kind === "quarter") {
    const y = Number(period.slice(0, 4));
    const q = Number(period.slice(-1));
    return q === 1 ? `${y - 1}-Q4` : `${y}-Q${q - 1}`;
  }
  if (kind === "year") return String(Number(period) - 1);
  if (kind === "range") {
    const [a, b] = period.split("_").map((x) => dayjs(x));
    const span = b.diff(a, "day") + 1;
    return `${a.subtract(span, "day").format("YYYY-MM-DD")}_${a.subtract(1, "day").format("YYYY-MM-DD")}`;
  }
  return dayjs(`${period}-01`).subtract(1, "month").format("YYYY-MM");
}

export function quarterOptions(count = 8): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  let value = currentQuarter();
  for (let i = 0; i < count; i++) {
    out.push({ value, label: periodLabel(value) });
    value = previousPeriod(value);
  }
  return out;
}

export function yearOptions(count = 4): { value: string; label: string }[] {
  const y = dayjs().year();
  return Array.from({ length: count }, (_, i) => ({ value: String(y - i), label: String(y - i) }));
}

export function periodMonth(period: string): string {
  return dayjs(`${period}-01`).format("MMMM");
}

export function prevPeriodMonth(period: string): string {
  return dayjs(`${period}-01`).subtract(1, "month").format("MMMM");
}
